import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import posthog from 'posthog-js'
import { supabase } from '@/shared/lib/supabase'
import { queryClient } from '@/shared/lib/queryClient'
import { db, clearLocalData } from '@/shared/lib/dexie'
import type { Organization } from '@/shared/types'

interface AuthContextValue {
  session: Session | null
  user: User | null
  loading: boolean
  org: Organization | null
  orgLoading: boolean
  setOrg: (org: Organization) => void
  displayName: string | null
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  org: null,
  orgLoading: true,
  setOrg: () => {},
  displayName: null,
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [org, setOrg] = useState<Organization | null>(null)
  const [orgLoading, setOrgLoading] = useState(false)
  const prevUserId = useRef<string | null | undefined>(undefined)
  const lastOrgFetchUserId = useRef<string | null>(null)
  const manualSignOut = useRef(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setOrgLoading(true)
      setSession(data.session)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Don't clear session when offline — access token may have expired without network to refresh
      if ((event === 'SIGNED_OUT' || (!session && event !== 'INITIAL_SESSION')) && !navigator.onLine && prevUserId.current !== undefined) {
        return
      }
      const newUserId = session?.user?.id ?? null
      if (prevUserId.current !== undefined && prevUserId.current !== newUserId) {
        queryClient.clear()
        clearLocalData().catch(() => {})
        if (event === 'SIGNED_OUT' && prevUserId.current !== null && !manualSignOut.current) {
          sessionStorage.setItem('shelvi_session_expired', '1')
        }
        manualSignOut.current = false
      }
      prevUserId.current = newUserId
      // USER_UPDATED fires when updateUser() changes metadata — org hasn't changed, skip re-fetch
      // TOKEN_REFRESHED fires on silent JWT rotation — org hasn't changed, skip re-fetch
      if (session && event !== 'USER_UPDATED' && event !== 'TOKEN_REFRESHED') setOrgLoading(true)
      setSession(session)
      if (!session) setOrg(null)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session?.user) {
      setOrg(null)
      setOrgLoading(false)
      lastOrgFetchUserId.current = null
      posthog.reset()
      return
    }
    // Same user re-emitting (e.g. USER_UPDATED from updateUser) — org unchanged, skip re-fetch
    if (lastOrgFetchUserId.current === session.user.id) {
      setOrgLoading(false)
      return
    }
    lastOrgFetchUserId.current = session.user.id
    setOrgLoading(true)

    if (!navigator.onLine) {
      db.organizations.where('owner_id').equals(session.user.id).first()
        .then(cached => {
          const resolved = (cached ?? null) as Organization | null
          setOrg(resolved)
          if (resolved) posthog.identify(resolved.id)
          setOrgLoading(false)
        })
        .catch(() => setOrgLoading(false))
      return
    }

    // Safe to filter client-side on owner_id — Supabase RLS policy
    // `owner_id = auth.uid()` enforces the same constraint server-side.
    // DO NOT drop that RLS policy; this query is only safe because of it.
    supabase
      .from('organizations')
      .select('*')
      .eq('owner_id', session.user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .then(async ({ data, error }) => {
        if (error && !navigator.onLine) {
          // Network failed mid-flight — fall back to Dexie
          db.organizations.where('owner_id').equals(session.user.id).first()
            .then(cached => {
              const resolved = (cached ?? null) as Organization | null
              setOrg(resolved)
              if (resolved) posthog.identify(resolved.id)
              setOrgLoading(false)
            })
            .catch(() => setOrgLoading(false))
          return
        }
        if ((data as Organization[] | null)?.[0]) {
          const resolved = (data as Organization[])[0]
          setOrg(resolved)
          posthog.identify(resolved.id)
          setOrgLoading(false)
          return
        }
        // Branch account — no org owns this user_id; check branches table
        const { data: branchData } = await supabase
          .from('branches')
          .select('id, master_org_id, name, owner_id, created_at, username, deleted_at')
          .eq('owner_id', session.user.id)
          .is('deleted_at', null)
          .limit(1)
        const branch = (branchData as Record<string, string | null>[] | null)?.[0]
        if (!branch) {
          setOrg(null)
          setOrgLoading(false)
          return
        }
        const { data: masterData } = await supabase
          .from('organizations')
          .select('timezone, currency')
          .eq('id', branch.master_org_id)
          .limit(1)
        const master = (masterData as { timezone: string | null; currency: string }[] | null)?.[0]
        const resolved: Organization = {
          id: branch.id as string,
          name: branch.name as string,
          owner_id: branch.owner_id as string,
          created_at: branch.created_at as string,
          timezone: master?.timezone ?? null,
          currency: master?.currency ?? 'PHP',
          master_org_id: branch.master_org_id as string,
          username: branch.username as string | null,
        }
        // Cache in Dexie so offline path finds this account
        db.organizations.put(resolved).catch(() => {})
        setOrg(resolved)
        posthog.identify(resolved.id)
        setOrgLoading(false)
      })
  }, [session])

  const displayName = session?.user?.user_metadata?.display_name ?? null

  async function signOut() {
    manualSignOut.current = true
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, org, orgLoading, setOrg, displayName, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
